import React from 'react';
import { cn } from '@/lib/utils';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';

export type ReactionFilter = 
  | 'trophy' 
  | 'grind' 
  | 'brain' 
  | 'clutch' 
  | 'fire_today'
  | null;

interface ReactionFilterChipsProps {
  activeFilter: ReactionFilter;
  onFilterChange: (filter: ReactionFilter) => void;
}

const REACTION_FILTERS: { id: ReactionFilter; emoji: string; label: string }[] = [
  { id: 'fire_today', emoji: '🔥', label: "Today's" },
  { id: 'trophy', emoji: '🏆', label: "Trophy'd" },
  { id: 'grind', emoji: '💪', label: 'Grind' },
  { id: 'brain', emoji: '🧠', label: 'IQ' },
  { id: 'clutch', emoji: '🎯', label: 'Clutch' },
];

export const ReactionFilterChips: React.FC<ReactionFilterChipsProps> = ({
  activeFilter,
  onFilterChange,
}) => {
  const handleClick = (filterId: ReactionFilter) => {
    if (activeFilter === filterId) {
      onFilterChange(null); // Deselect
    } else {
      onFilterChange(filterId);
    }
  };

  return (
    <ScrollArea className="w-full whitespace-nowrap">
      <div className="flex gap-2 pb-2">
        {REACTION_FILTERS.map((filter) => (
          <button
            key={filter.id}
            onClick={() => handleClick(filter.id)}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all',
              'border hover:border-primary/50',
              activeFilter === filter.id
                ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                : 'bg-background text-muted-foreground border-border hover:text-foreground hover:bg-accent'
            )}
          >
            <span className="text-base">{filter.emoji}</span>
            <span>Most {filter.label}</span>
          </button>
        ))}
      </div>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
};

// Map filter IDs to reaction type keys used in reactions_count JSONB
export const getReactionTypeKey = (filter: ReactionFilter): string | null => {
  switch (filter) {
    case 'trophy':
      return 'trophy';
    case 'grind':
      return 'grind';
    case 'brain':
      return 'brain';
    case 'clutch':
      return 'clutch';
    case 'fire_today':
      return 'fire';
    default:
      return null;
  }
};

// Get empty state message for each filter
export const getEmptyStateMessage = (filter: ReactionFilter): { title: string; subtitle: string } => {
  switch (filter) {
    case 'trophy':
      return {
        title: 'No posts with 🏆 Trophy reactions yet',
        subtitle: 'Be the first to earn one with an elite play!',
      };
    case 'grind':
      return {
        title: 'No posts with 💪 Grind reactions yet',
        subtitle: 'Show your hard work and earn that respect!',
      };
    case 'brain':
      return {
        title: 'No posts with 🧠 IQ reactions yet',
        subtitle: 'Share a smart play and get recognized!',
      };
    case 'clutch':
      return {
        title: 'No posts with 🎯 Clutch reactions yet',
        subtitle: 'Post your clutch moments to get featured!',
      };
    case 'fire_today':
      return {
        title: 'No 🔥 fire posts today yet',
        subtitle: 'Be the first to light up the feed today!',
      };
    default:
      return {
        title: 'No posts yet',
        subtitle: 'Be the first to share something!',
      };
  }
};
